import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { classNames } from './classNames';
import { habitaControlBorderClass } from './HabitaSurface';

const buttonVariants = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-primary/60',
  secondary: `border ${habitaControlBorderClass} bg-card text-textPrimary hover:bg-accent disabled:text-textSecondary/60`,
  ghost: 'text-textSecondary hover:bg-accent hover:text-textPrimary disabled:text-textSecondary/50',
  warning: 'bg-amber-600 text-white hover:bg-amber-700 disabled:bg-amber-500/70',
  success: 'bg-emerald-700 text-white hover:bg-emerald-800 disabled:bg-emerald-600/70',
  danger: 'border border-red-300/40 bg-card text-red-700 hover:bg-red-500/10 disabled:text-red-300 dark:text-red-200',
};

const buttonSizes = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  icon: 'h-8 w-8 p-0 text-sm',
};

interface HabitaButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: string;
  variant?: keyof typeof buttonVariants;
  size?: keyof typeof buttonSizes;
}

export function HabitaButton({
  children,
  className,
  icon,
  size = 'md',
  type = 'button',
  variant = 'secondary',
  ...props
}: HabitaButtonProps) {
  return (
    <button
      {...props}
      type={type}
      className={classNames(
        'inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-instrument font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-75',
        buttonVariants[variant],
        buttonSizes[size],
        className
      )}
    >
      {icon && <i className={`${icon} text-base`} aria-hidden />}
      {children}
    </button>
  );
}

interface HabitaTextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  containerClassName?: string;
}

function hasExplicitContainerWidth(className: string | undefined): boolean {
  return Boolean(className?.match(/(?:^|\s)!?(?:w-|basis-|flex-1|flex-auto|flex-initial|flex-none)/));
}

export function HabitaTextInput({ className, containerClassName, ...props }: HabitaTextInputProps) {
  return (
    <div className={classNames(!hasExplicitContainerWidth(containerClassName) && 'w-full', containerClassName)}>
      <input
        {...props}
        className={classNames(
          `h-9 w-full rounded-md border ${habitaControlBorderClass} bg-card px-3 font-instrument text-sm text-textPrimary outline-none transition-colors placeholder:text-textSecondary/70 focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-muted`,
          className
        )}
      />
    </div>
  );
}

interface HabitaTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export function HabitaTextarea({ className, label, ...props }: HabitaTextareaProps) {
  return (
    <label className="block space-y-1">
      {label && (
        <span className="font-instrument text-[11px] font-medium uppercase tracking-wide text-textSecondary">
          {label}
        </span>
      )}
      <textarea
        {...props}
        className={classNames(
          `w-full rounded-md border ${habitaControlBorderClass} bg-card px-3 py-2 font-instrument text-sm text-textPrimary outline-none transition-colors placeholder:text-textSecondary/70 focus:border-primary focus:ring-2 focus:ring-primary/20`,
          className
        )}
      />
    </label>
  );
}

export interface HabitaSelectOption {
  label: string;
  value: string;
}

interface HabitaSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: HabitaSelectOption[];
}

export function HabitaSelect({ className, options, ...props }: HabitaSelectProps) {
  return (
    <select
      {...props}
      className={classNames(
        `h-9 rounded-md border ${habitaControlBorderClass} bg-card px-3 pr-8 font-instrument text-sm text-textPrimary outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20`,
        className
      )}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export interface HabitaViewToggleOption<TValue extends string> {
  icon: string;
  label: string;
  value: TValue;
}

export function HabitaViewToggle<TValue extends string>({
  className,
  label,
  onChange,
  options,
  showLabels = false,
  value,
}: {
  className?: string;
  label: string;
  onChange: (value: TValue) => void;
  options: Array<HabitaViewToggleOption<TValue>>;
  showLabels?: boolean;
  value: TValue;
}) {
  return (
    <div
      aria-label={label}
      className={classNames(
        'inline-flex h-9 shrink-0 items-center rounded-md border border-border-subtle bg-muted/45 p-0.5',
        className
      )}
      role="group"
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-label={option.label}
            aria-pressed={isActive}
            className={classNames(
              'inline-flex h-8 items-center justify-center gap-1.5 rounded px-2 transition-colors',
              !showLabels && 'w-8 px-0',
              isActive
                ? 'bg-primary text-white shadow-sm'
                : 'text-textSecondary hover:bg-surface hover:text-textPrimary'
            )}
            onClick={() => onChange(option.value)}
          >
            <i className={`${option.icon} text-base`} aria-hidden />
            {showLabels && <span className="font-instrument text-xs font-medium">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

interface HabitaActiveFilterChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick'> {
  label: string;
  onRemove: () => void;
  value: string;
}

export function HabitaActiveFilterChip({
  className,
  label,
  onRemove,
  type = 'button',
  value,
  ...props
}: HabitaActiveFilterChipProps) {
  return (
    <button
      {...props}
      type={type}
      onClick={onRemove}
      className={classNames(
        'inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-200/70 bg-emerald-50 px-2.5 py-1 font-instrument text-xs text-emerald-950 shadow-sm shadow-emerald-950/5 transition-colors hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-emerald-300/20 dark:bg-emerald-300/10 dark:text-emerald-50 dark:hover:bg-emerald-300/15',
        className
      )}
      aria-label={props['aria-label'] ?? `Remover filtro ${label}`}
    >
      <span className="text-emerald-700 dark:text-emerald-200/80">{label}:</span>
      <span className="truncate">{value}</span>
      <i className="ri-close-line text-sm text-emerald-700 dark:text-emerald-200/80" aria-hidden />
    </button>
  );
}

export function HabitaCheckbox({
  checked,
  children,
  className,
  disabled = false,
  onCheckedChange,
}: {
  checked: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={classNames(
        'flex items-center gap-3 px-3 py-2 font-instrument transition-colors',
        disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-accent',
        className
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onCheckedChange(event.target.checked)}
        className="h-4 w-4 rounded border-border-subtle accent-primary"
      />
      {children}
    </label>
  );
}

export function HabitaDialog({
  children,
  footer,
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
}: {
  children: ReactNode;
  footer?: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    if (!isOpen) return undefined;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClass = size === 'xl' ? 'max-w-5xl' : size === 'lg' ? 'max-w-3xl' : 'max-w-xl';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        onClick={onClose}
        aria-label="Fechar modal"
      />
      <section
        role="dialog"
        aria-modal="true"
        className={classNames('relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-lg bg-card text-textPrimary shadow-xl', sizeClass)}
      >
        <div className="shrink-0 border-b border-border-subtle px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-fraunces text-lg font-semibold text-textPrimary">{title}</h2>
              {description && <p className="mt-1 font-instrument text-sm text-textSecondary">{description}</p>}
            </div>
            <HabitaButton
              aria-label="Fechar"
              icon="ri-close-line"
              onClick={onClose}
              size="icon"
              variant="ghost"
            />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border-subtle px-5 py-4">{footer}</div>}
      </section>
    </div>
  );
}

export function HabitaPagination({
  currentPage,
  onPageChange,
  onPageSizeChange,
  pageSize,
  pageSizeOptions = [10, 20, 50],
  totalItems,
  totalItemsLabel,
  totalPages,
}: {
  currentPage: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  totalItems: number;
  totalItemsLabel?: string;
  totalPages: number;
}) {
  const visiblePages = Array.from({ length: totalPages }, (_, index) => index + 1)
    .filter((page) =>
      page === 1 ||
      page === totalPages ||
      Math.abs(page - currentPage) <= 2
    );

  return (
    <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
      <span className="font-instrument text-xs text-textSecondary">
        Página {currentPage} de {totalPages} · {totalItemsLabel ?? totalItems} resultado(s)
      </span>
      <div className="inline-flex flex-wrap items-center gap-2">
        {pageSize && onPageSizeChange && (
          <label className="inline-flex items-center gap-2 font-instrument text-xs text-textSecondary">
            <span>Itens por página</span>
            <HabitaSelect
              aria-label="Itens por página"
              className="h-8 w-24 text-xs"
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              options={pageSizeOptions.map((option) => ({
                label: String(option),
                value: String(option),
              }))}
              value={String(pageSize)}
            />
          </label>
        )}

        <div className="inline-flex flex-wrap items-center gap-1">
          <HabitaButton
            icon="ri-arrow-left-s-line"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            size="sm"
          >
            Anterior
          </HabitaButton>
          {visiblePages.map((page, index) => (
            <span key={page} className="inline-flex items-center gap-1">
              {index > 0 && page - visiblePages[index - 1] > 1 && (
                <span className="px-1 font-instrument text-xs text-textSecondary" aria-hidden>
                  ...
                </span>
              )}
              <HabitaButton
                onClick={() => onPageChange(page)}
                size="icon"
                variant={page === currentPage ? 'primary' : 'ghost'}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </HabitaButton>
            </span>
          ))}
          <HabitaButton
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            size="sm"
          >
            Próxima
            <i className="ri-arrow-right-s-line text-base" aria-hidden />
          </HabitaButton>
        </div>
      </div>
    </div>
  );
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

export function CopyValueButton({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  async function handleCopy() {
    const didCopy = await copyText(value);
    if (!didCopy) return;

    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
    }

    setCopied(true);
    resetTimerRef.current = window.setTimeout(() => {
      setCopied(false);
      resetTimerRef.current = null;
    }, 1600);
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={classNames(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-card',
        copied ? 'bg-emerald-50 text-emerald-700' : 'text-textSecondary hover:text-textPrimary',
        className
      )}
      aria-label={`Copiar ${label}`}
      title={copied ? 'Copiado' : `Copiar ${label}`}
    >
      <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`} aria-hidden />
    </button>
  );
}
